import { Document } from '@contentful/rich-text-types';
import React, { FC, useMemo } from 'react';

import CustomLink from '@/components/custom-link';
import Image from '@/components/image';
import RichTextRenderer from '@/components/rich-text-renderer';
import useBreakpoint from '@/lib/hooks/use-breakpoint';
import { GridEntryTypeCms } from '@/types/cms';
import { ImageAsset, Link } from '@/types/views/generic';

import styles from './styles';

interface ImageWithTextProps {
  title?: string;
  link?: Link;
  image?: ImageAsset;
  mobileImage?: ImageAsset;
  text?: Document;
  type?: GridEntryTypeCms;
}

const INFO_IMAGE_WIDTH = 70;

const ImageWithText: FC<ImageWithTextProps> = ({
  image,
  link,
  title,
  text,
  mobileImage,
  type = GridEntryTypeCms.Info,
}) => {
  const { isMobile } = useBreakpoint();

  const isInfoType = type === GridEntryTypeCms.Info;
  const isCentered = !title && !text;

  const selectedImage = useMemo(
    () => (isMobile && mobileImage ? mobileImage : image),
    [isMobile, mobileImage, image],
  );

  const imageDescription = selectedImage?.description || title || 'Image';
  const imageWidth = selectedImage?.width ?? 0;
  const imageHeight = selectedImage?.height ?? 0;
  // External URL-only assets have no Contentful dimensions; intrinsic layout
  // collapses to 0×0 unless we use fill + object-fit instead.
  const hasIntrinsicDimensions = imageWidth > 0 && imageHeight > 0;

  const calculatedHeight =
    isInfoType && imageWidth > 0
      ? (INFO_IMAGE_WIDTH * imageHeight) / imageWidth
      : imageHeight;

  let imageElement: JSX.Element | null = null;

  if (selectedImage && hasIntrinsicDimensions) {
    imageElement = (
      <Image
        src={selectedImage.src}
        fallbackSrc={selectedImage.fallbackSrc}
        alt={imageDescription}
        useIntrinsicSize={true}
        width={isInfoType ? INFO_IMAGE_WIDTH : imageWidth}
        height={calculatedHeight}
        className={styles.image(isInfoType, isCentered)}
        basicLoader={selectedImage.isExternal}
      />
    );
  } else if (selectedImage) {
    imageElement = (
      <div className={styles.image(isInfoType, isCentered)}>
        <div
          className={
            isInfoType
              ? 'relative h-[70px] w-[70px]'
              : 'relative h-[135px] w-full'
          }
        >
          <Image
            src={selectedImage.src}
            fallbackSrc={selectedImage.fallbackSrc}
            alt={imageDescription}
            objectFit="contain"
            basicLoader
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container(isInfoType, isCentered)}>
      {imageElement &&
        (link ? (
          <CustomLink {...link}>{imageElement}</CustomLink>
        ) : (
          imageElement
        ))}

      <div
        className={isInfoType ? 'tesn:py-4 teair:py-4' : 'tesn:p-4 teair:p-4'}
      >
        {title && <h5 className={styles.title(isInfoType)}>{title}</h5>}

        {text && (
          <RichTextRenderer
            body={text}
            className={styles.text}
            openLinksInNewTab
          />
        )}
      </div>
    </div>
  );
};

export default ImageWithText;
